import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageContainer } from "@/components/ui/PageContainer";
import { supabase } from "@/integrations/supabase/client";

interface ResultItem {
  id: string;
  name?: string | null;
  status: string;
  reason?: string;
  newUrl?: string;
  to?: string;
  source?: string;
}

export default function AdminBackfillFundImages() {
  const [dryRun, setDryRun] = useState(true);
  const [force, setForce] = useState(false);
  const [convertToWebp, setConvertToWebp] = useState(true);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [fundIds, setFundIds] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [results, setResults] = useState<ResultItem[]>([]);

  const handleRun = async () => {
    setLoading(true);
    setSummary(null);
    setResults([]);
    try {
      const ids = fundIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const { data, error } = await supabase.functions.invoke("backfill-fund-images", {
        body: {
          dryRun,
          limit,
          offset,
          fundIds: ids.length ? ids : undefined,
          force,
          convertToWebp,
        },
      });

      if (error) throw error;
      setSummary(data?.summary);
      setResults(data?.results ?? []);
      toast.success("Backfill completed");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to run backfill");
    } finally {
      setLoading(false);
    }
  };

  const handleProcessInBucket = async () => {
    setLoading(true);
    setSummary(null);
    setResults([]);
    try {
      const { data: funds, error } = await (supabase as any)
        .from('funds')
        .select('id,name,fund_image_url')
        .not('fund_image_url', 'is', null)
        .ilike('fund_image_url', '%/storage/v1/object/public/%')
        .limit(1000);

      if (error) throw error;

      const ids = (funds ?? []).map((f: any) => f.id);
      if (!ids.length) {
        toast.info('No in-bucket images found');
        return;
      }

      const { data, error: fnErr } = await supabase.functions.invoke('backfill-fund-images', {
        body: {
          dryRun: false,
          limit: ids.length,
          offset: 0,
          fundIds: ids,
          force: true,
          convertToWebp: true,
        },
      });

      if (fnErr) throw fnErr;

      setSummary(data?.summary);
      setResults(data?.results ?? []);
      toast.success(`Processed ${ids.length} image(s)`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to process images');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer maxWidthClassName="max-w-[960px]">
      <header>
        <h1 className="text-xl font-semibold">Backfill Fund Images</h1>
        <p className="text-sm opacity-80">One-off tool to normalize existing fund images into storage.</p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <label className="text-sm">Dry run</label>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            <span className="text-sm">Do not upload/update DB, just preview</span>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm">Force re-upload (re-normalize)</label>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            <span className="text-sm">Process even if the URL is already in our bucket</span>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm">Convert to WebP</label>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={convertToWebp}
              onChange={(e) => setConvertToWebp(e.target.checked)}
            />
            <span className="text-sm">Upload a canonical WebP (80 quality) and point DB to it</span>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm">Limit</label>
          <Input
            type="number"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            min={1}
            max={500}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm">Offset</label>
          <Input
            type="number"
            value={offset}
            onChange={(e) => setOffset(Number(e.target.value))}
            min={0}
          />
        </div>
        <div className="space-y-2 sm:col-span-2 lg:col-span-3">
          <label className="text-sm">Fund IDs (comma-separated, optional)</label>
          <Input
            placeholder="uuid-1, uuid-2, ..."
            value={fundIds}
            onChange={(e) => setFundIds(e.target.value)}
          />
        </div>
      </section>

      <div className="flex gap-3">
        <Button onClick={handleRun} disabled={loading}>
          {loading ? "Running..." : "Run backfill"}
        </Button>
        <Button variant="outline" onClick={handleProcessInBucket} disabled={loading}>
          {loading ? "Processing..." : "Process in-bucket → WebP"}
        </Button>
        {!dryRun && (
          <span className="text-sm opacity-70">This will upload files and update DB</span>
        )}
      </div>

      {summary && (
        <section className="space-y-2">
          <h2 className="font-medium">Summary</h2>
          <pre className="text-xs bg-white/5 p-3 rounded-md overflow-auto">
            {JSON.stringify(summary, null, 2)}
          </pre>
        </section>
      )}

      {results.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-medium">Results</h2>
          <div className="max-h-96 overflow-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-black/5">
                <tr>
                  <th className="text-left p-2">Fund</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="p-2 break-all">{r.name ?? r.id}</td>
                    <td className="p-2">{r.status}</td>
                    <td className="p-2 break-all">{r.reason ?? r.newUrl ?? r.to ?? r.source ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </PageContainer>
  );
}
